import React, { useEffect } from 'react';
import {
  Text,
  Link,
  Form,
  TextFieldControl,
  TagsInputControl,
  SelectControl,
  CheckboxControl,
  Stack,
  withTranslation,
  Trans,
  Information,
  Button,
  Tooltip,
} from '@piwikpro/ui-components';
import { TFunction } from 'react-i18next';
import { FormikActions } from 'formik';
import * as Yup from 'yup';
import { withAccess, withInject } from '@piwikpro/platform';
import { connect } from 'react-redux';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import {
  CookieInformationService,
  cookieInformationAccountConnectFormShown,
} from '../../CookieInformationService';

const consentLanguageOptions = [
  { id: 'ar', name: 'Arabic', value: 'ar' },
  { id: 'bg', name: 'Bulgarian', value: 'bg' },
  { id: 'ca', name: 'Catalan', value: 'ca' },
  { id: 'cs', name: 'Czech', value: 'cs' },
  { id: 'da', name: 'Danish', value: 'da' },
  { id: 'de', name: 'German', value: 'de' },
  { id: 'el', name: 'Greek', value: 'el' },
  { id: 'en', name: 'English', value: 'en' },
  { id: 'es', name: 'Spanish', value: 'es' },
  { id: 'et', name: 'Estonian', value: 'et' },
  { id: 'fi', name: 'Finnish', value: 'fi' },
  { id: 'fr', name: 'French', value: 'fr' },
  { id: 'he', name: 'Hebrew', value: 'he' },
  { id: 'hi', name: 'Hindi', value: 'hi' },
  { id: 'hr', name: 'Croatian', value: 'hr' },
  { id: 'hu', name: 'Hungarian', value: 'hu' },
  { id: 'id', name: 'Indonesian', value: 'id' },
  { id: 'is', name: 'Icelandic', value: 'is' },
  { id: 'it', name: 'Italian', value: 'it' },
  { id: 'ja', name: 'Japanese', value: 'ja' },
  { id: 'kl', name: 'Greenlandic', value: 'kl' },
  { id: 'ko', name: 'Korean', value: 'ko' },
  { id: 'lt', name: 'Lithuanian', value: 'lt' },
  { id: 'lv', name: 'Latvian', value: 'lv' },
  { id: 'ms', name: 'Malay', value: 'ms' },
  { id: 'nb', name: 'Norwegian Bokmål', value: 'nb' },
  { id: 'nl', name: 'Dutch', value: 'nl' },
  { id: 'nn', name: 'Norwegian nynorsk', value: 'nn' },
  { id: 'no', name: 'Norwegian macrolanguage', value: 'no' },
  { id: 'pl', name: 'Polish', value: 'pl' },
  { id: 'pt', name: 'Portuguese', value: 'pt' },
  { id: 'ro', name: 'Romanian', value: 'ro' },
  { id: 'ru', name: 'Russian', value: 'ru' },
  { id: 'sk', name: 'Slovak', value: 'sk' },
  { id: 'sl', name: 'Slovenian', value: 'sl' },
  { id: 'sq', name: 'Albanian', value: 'sq' },
  { id: 'sr', name: 'Serbian', value: 'sr' },
  { id: 'sv', name: 'Swedish', value: 'sv' },
  { id: 'th', name: 'Thai', value: 'th' },
  { id: 'tr', name: 'Turkish', value: 'tr' },
  { id: 'tw', name: 'Chinese (Taiwan)', value: 'tw' },
  { id: 'uk', name: 'Ukrainian', value: 'uk' },
  { id: 'vi', name: 'Vietnamese', value: 'vi' },
  { id: 'zh', name: 'Chinese', value: 'zh' },
];

interface InjectedProps {
  cookieInformation: CookieInformationService
  accessManager: any
  t: TFunction
}

interface Props extends InjectedProps {
  loggedUserEmail: SessionState['email'] | null
  t: TFunction
  domains?: string[]
  onDirtyChange?: (modified: boolean) => void
}

interface FormValues {
  email: string
  domains: string[]
  consentLanguage: string
  termsAndConditions: boolean
  acceptedEmailsAndOffers: boolean
  generalError?: string
}

interface FormContentProps {
  errors: { generalError?: string }
  submitForm: () => void
  isValid: boolean
  isSubmitting: boolean
  dirty: boolean
  values: FormValues
  setFieldTouched: (field: string, touched?: boolean) => void
  validateForm: () => Promise<any>
  onDirtyChange?: (modified: boolean) => void
  t: TFunction
  isSubmitAllowedByRole: boolean
  showConnectForm: () => void
  initialDomains: string[]
  domainMustBeCanonicalMessage: string
}

// Requires domain in format: something.something
// rejects http:// or https:// prefixes
// rejects paths (forward slashes)
const CANONICAL_DOMAIN_REGEX = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/;

export const isCanonicalDomain = (domain: string): boolean => (
  !/^www\./i.test(domain) // rejects www. prefix
  && CANONICAL_DOMAIN_REGEX.test(domain)
);

const FormContent: React.FC<FormContentProps> = ({
  errors,
  submitForm,
  isValid,
  isSubmitting,
  dirty,
  values,
  setFieldTouched,
  validateForm,
  onDirtyChange,
  t,
  isSubmitAllowedByRole,
  showConnectForm,
  initialDomains,
  domainMustBeCanonicalMessage,
}) => {
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty]);

  const domainsList = values.domains ?? initialDomains;
  const domainsErrors: string[] = domainsList.map((domain: string) => (
    (domain && !isCanonicalDomain(domain)) ? domainMustBeCanonicalMessage : ''
  ));
  const hasInvalidDomains = domainsErrors.some(msg => msg !== '');

  useEffect(() => {
    if (hasInvalidDomains && domainsList.length > 0) {
      setFieldTouched('domains', true);
      validateForm();
    }
  }, [hasInvalidDomains, domainsList.length, setFieldTouched, validateForm]);

  return (
    <Stack vertical>
      {errors.generalError && (
        <Stack.Item fill>
          <Information type="error">{errors.generalError as string}</Information>
        </Stack.Item>
      )}
      <Stack.Item fill>
        <TextFieldControl
          label={t('cookieInformation.createAccount.emailLabel')}
          name="email"
          required
          info={t('cookieInformation.createAccount.emailInfo')}
        />
      </Stack.Item>
      <Stack.Item fill>
        <TagsInputControl
          label={t('cookieInformation.createAccount.domainLabel')}
          name="domains"
          required
          info={t('cookieInformation.createAccount.domainInfo')}
          splitSeparators={[',', ';', '\n']}
          error={hasInvalidDomains ? domainsErrors : []}
        />
      </Stack.Item>
      <Stack.Item fill>
        <SelectControl
          label={t('cookieInformation.createAccount.consentLanguageLabel')}
          name="consentLanguage"
          required
          options={[{
            id: 'consent-language-select',
            name: '',
            items: consentLanguageOptions,
          }]}
          info={t('cookieInformation.createAccount.consentLanguageInfo')}
        />
      </Stack.Item>
      <Stack.Item previousSpacing="wide">
        <CheckboxControl
          name="termsAndConditions"
          label={(
            <Trans
              i18nKey={t('cookieInformation.createAccount.termsAndConditions')}
              parent="span"
              components={{
                linkTermsAndConditions: <Link href="https://cookieinformation.com/master-service-agreement/" target="_blank">Master Terms of Service</Link>,
                linkDataProcessingAgreement: <Link href="https://cookieinformation.com/business-data-processing-agreement/" target="_blank">Data Processing Agreement</Link>,
                linkCookieAndPrivacyPolicy: <Link href="https://cookieinformation.com/cookie-and-privacy-policy/" target="_blank">Cookie & Privacy Policy</Link>,
                required: <span style={{ color: 'var(--vRed)' }}>*</span>,
              }}
            />
          )
          }
        />
      </Stack.Item>
      <Stack.Item previousSpacing="narrow" fill>
        <CheckboxControl
          name="acceptedEmailsAndOffers"
          label={t('cookieInformation.createAccount.marketingEmails')}
        />
      </Stack.Item>
      <Stack.Item fill previousSpacing="wide">
        <Tooltip
          content={!isSubmitAllowedByRole && t('accessControl.permissions.role.owner.required.info')}
        >
          <Button
            appearance="primary"
            text={t('cookieInformation.createAccount.createButton')}
            fullWidth
            onClick={submitForm}
            disabled={!isSubmitAllowedByRole || !isValid || isSubmitting}
          />
        </Tooltip>
      </Stack.Item>
      <Stack.Item fill>
        <Stack vertical spacing="narrow" horizontalAlignment="middle">
          <Stack.Item>
            <Text as="p">
              <Trans
                i18nKey={t('cookieInformation.createAccount.alreadyHaveAccount')}
                parent="span"
                components={{
                  createAccountLink: <Link onClick={showConnectForm}>Text</Link>,
                }}
              />
            </Text>
          </Stack.Item>
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

const ConnectedFormContent = connect(null, {
  showConnectForm: cookieInformationAccountConnectFormShown,
})(FormContent);

const errorKeyMap: Record<string, string> = {
  user_email: 'email',
  domains: 'domains',
  site_languages: 'consentLanguage',
  accepted_emails_and_offers: 'acceptedEmailsAndOffers',
};

const generalErrorsKeyMap = (t: TFunction) => ({
  // left side must be compliant with CI response message
  'Resource with given data already exists': t('cookieInformation.createAccount.errors.domainAlreadyExists'),
  "Resource couldn't be created with given data": t('cookieInformation.createAccount.errors.emailAlreadyLinked'),
  'Something went wrong, please try again later': t('cookieInformation.createAccount.errors.generalError'),
});

function mapApiErrorsToFormFields(apiErrors: Record<string, any>, t: TFunction): Record<string, any> {
  const hasNestedStructure = Object.values(apiErrors).some(
    value => typeof value === 'object' && value !== null && !Array.isArray(value),
  );

  if (hasNestedStructure) {
    return { generalError: t('cookieInformation.createAccount.errors.generalError') };
  }

  const mapped: Record<string, any> = {};
  const entries = Object.entries(apiErrors);
  const hasInvalidStructure = entries.some(([, value]) => {
    const rawErrorMessage = Array.isArray(value) ? value[0] : value;
    return (
      (typeof rawErrorMessage === 'object' && rawErrorMessage !== null && !Array.isArray(rawErrorMessage))
      || typeof rawErrorMessage !== 'string'
    );
  });

  if (hasInvalidStructure) {
    return { generalError: t('cookieInformation.createAccount.errors.generalError') };
  }

  entries.forEach(([key, value]) => {
    const formKey = errorKeyMap[key] || key;
    const rawErrorMessage = Array.isArray(value) ? value[0] : value;
    let errorMessage = rawErrorMessage.length > 0
      ? rawErrorMessage.charAt(0).toUpperCase() + rawErrorMessage.slice(1)
      : rawErrorMessage;
    if (errorMessage.length > 0 && !errorMessage.endsWith('.')) {
      errorMessage = `${errorMessage}.`;
    }
    mapped[formKey] = errorMessage;
  });
  return mapped;
}

export const CookieInformationCreateAccount: React.FC<Props> = ({
  loggedUserEmail,
  cookieInformation,
  accessManager,
  t,
  domains,
  onDirtyChange,
}) => {
  const isSubmitAllowedByRole = accessManager.isGranted({ role: 'OWNER' });
  const handleSubmit = async (values: FormValues, { setSubmitting, setErrors }: FormikActions<FormValues>) => {
    try {
      await cookieInformation.createAccount({
        userLanguage: 'en',
        email: values.email,
        domains: values.domains,
        consentLanguage: [values.consentLanguage],
        acceptedEmailsAndOffers: values.acceptedEmailsAndOffers,
      });
    } catch (error: any) {
      if (error.errors && error.errors.detail) {
        setErrors({ generalError: generalErrorsKeyMap(t)[error.errors.detail] || error.errors.detail });
      } else {
        setErrors(mapApiErrorsToFormFields(error.errors, t));
      }
    }

    setSubmitting(false);
  };

  const domainMustBeCanonicalMessage = t('cookieInformation.createAccount.validation.canonicalFormatTooltip');

  const validationSchema = () => Yup.object().shape({
    email: Yup.string().email(t('cookieInformation.createAccount.validation.invalidEmail')).required(t('cookieInformation.createAccount.validation.emailRequired')),
    termsAndConditions: Yup.bool().oneOf([true], t('cookieInformation.createAccount.validation.termsRequired')),
    domains: Yup.array()
      .min(1, t('cookieInformation.createAccount.validation.domainsRequired'))
      .of(
        Yup.string().test(
          'canonical-domain',
          domainMustBeCanonicalMessage,
          value => !value || isCanonicalDomain(value),
        ),
      ),
  });

  const initialDomains = domains || [];

  return (
    <Form
      id="cookie-information-create-account-form"
      initialValues={{
        email: loggedUserEmail,
        domains: initialDomains,
        consentLanguage: 'en',
        termsAndConditions: false,
        acceptedEmailsAndOffers: false,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      render={({
        errors, submitForm, isValid, isSubmitting, dirty, values, setFieldTouched, validateForm,
      }) => (
        <ConnectedFormContent
          errors={errors}
          submitForm={submitForm}
          isValid={isValid}
          isSubmitting={isSubmitting}
          dirty={dirty}
          values={values}
          setFieldTouched={setFieldTouched}
          validateForm={validateForm}
          onDirtyChange={onDirtyChange}
          t={t}
          isSubmitAllowedByRole={isSubmitAllowedByRole}
          initialDomains={initialDomains}
          domainMustBeCanonicalMessage={domainMustBeCanonicalMessage}
        />
      )}
    />
  );
};

export default withInject<InjectedProps>({
  cookieInformation: 'CookieInformationCrate.CookieInformationService',
})(withTranslation(['administration'])(withAccess(CookieInformationCreateAccount)));
